import { api } from './api';
import { compressImage } from './image';

// ---------- vehicle expenses ----------

export type ExpenseKind =
  | 'Fuel' | 'Service' | 'Repair' | 'Tyres'
  | 'Insurance' | 'RtoAndPermits' | 'Other';

/** ⚠ Order matters — this is the order the dropdown renders, commonest first. */
export const EXPENSE_KINDS: { value: ExpenseKind; label: string }[] = [
  { value: 'Fuel',          label: 'Diesel / Petrol' },
  { value: 'Service',       label: 'Service' },
  { value: 'Repair',        label: 'Repair' },
  { value: 'Tyres',         label: 'Tyres' },
  { value: 'Insurance',     label: 'Insurance' },
  { value: 'RtoAndPermits', label: 'RTO / FC / Permit' },
  { value: 'Other',         label: 'Other' },
];

export interface VehicleExpense {
  id: string;
  vehicleId: string;
  vehicleName: string;
  kind: ExpenseKind;
  spentOn: string;
  /** ⚠ PAISE. Divide by 100 to display. */
  amountPaise: number;
  vendor: string | null;
  billNumber: string | null;
  odometerKm: number | null;
  litres: number | null;
  hasBill: boolean;
  hasMeter: boolean;
  /** Who was accountable for the vehicle when this was spent — snapshotted, not live. */
  responsibleName: string | null;
  note: string | null;
  recordedAt: string;
}

export interface VehicleCost {
  vehicleId: string;
  vehicleName: string;
  registrationNumber: string | null;
  totalPaise: number;
  fuelPaise: number;
  maintenancePaise: number;
  otherPaise: number;
  totalLitres: number | null;
  distanceKm: number | null;
  /** ⚠ NULL means "not enough readings to know" — never render it as zero. */
  kmPerLitre: number | null;
  costPerKm: number | null;
}

export interface RecordExpenseBody {
  vehicleId: string;
  kind: ExpenseKind;
  spentOn: string;
  amountPaise: number;
  vendor: string | null;
  billNumber: string | null;
  odometerKm: number | null;
  litres: number | null;
  note: string | null;
}

export const expensesApi = {
  list: (vehicleId: string | null, from: string, to: string) => {
    const p = new URLSearchParams({ from, to });
    if (vehicleId) p.set('vehicleId', vehicleId);
    return api.get<VehicleExpense[]>(`/api/transport/expenses?${p}`);
  },

  record: (body: RecordExpenseBody) =>
    api.post<VehicleExpense>('/api/transport/expenses', body),

  remove: (id: string) => api.del<void>(`/api/transport/expenses/${id}`),

  /**
   * Attach a bill or meter photo.
   *
   * ⚠ Compressed BEFORE upload. A phone camera produces 4–6 MB; on a rural connection that is a
   * slow upload the clerk will blame on the app. compressImage fails open, so an unsupported
   * format still uploads at full size rather than failing.
   */
  attachPhoto: async (id: string, which: 'bill' | 'meter', file: File) => {
    const small = await compressImage(file);
    const fd = new FormData();
    fd.append('file', small);
    return api.postForm<VehicleExpense>(`/api/transport/expenses/${id}/photo/${which}`, fd);
  },

  /**
   * ⚠ Fetched as a blob, not linked. The endpoint checks who is asking, and an
   * `<img src="/api/...">` cannot carry a bearer token.
   */
  photoBlob: (id: string, which: 'bill' | 'meter') =>
    api.blob(`/api/transport/expenses/${id}/photo/${which}`),

  costReport: (from: string, to: string) =>
    api.get<VehicleCost[]>(`/api/transport/cost-report?from=${from}&to=${to}`),
};
